/**
 * crmInboxApi — the CRM SMS messenger surface (design §5).
 *
 *   GET    /crm/inbox/person/:personId/thread          → { sessionId, messages }
 *   POST   /crm/inbox/sessions/:sessionId/reply        → send an operator reply
 *   DELETE /crm/inbox/sessions/:sessionId/messages/:id → soft-delete (tombstone)
 *
 * The per-prospect thread reads ONLY through the unified resolver on the server
 * (resolvePersonSmsThreadId) — the 3c condition. Server injects community scope.
 */
import { baseApi } from "./baseApi";

export interface InboxThreadMessage {
  message_id: string;
  role: string;
  sender_type: string | null;
  content: string | null;
  sender_user_id: string | null;
  sender_user_name: string | null;
  sms_delivery_status: string | null;
  created_at: string;
  /** Non-null when soft-deleted: the content is withheld, this records who/when (R20). */
  deleted: { by: string | null; at: string } | null;
}

export interface InboxSessionRow {
  session_id: string;
  visitor_phone: string | null;
  handoff_status: string | null;
  status: string | null;
  is_read: boolean | null;
  last_message_at: string | null;
  prospect_id: string | null;
  prospect_name: string | null;
  community_id: string | null;
  /** Channel ('sms' | 'phone' | 'whatsapp' | 'web' | 'email') for the row's channel icon. */
  channel: string | null;
  /** Last-message preview + count for the conversation-list row. */
  preview_text: string | null;
  message_count: number;
  /** 'ambiguous' when the conversation is an attribution hold — drives the assign-dialog
   *  mode (ambiguous → link a candidate prospect; otherwise → resolve a community). */
  attribution_status: string | null;
  /** COMMS-2c: 'prospect' | 'referrer' — a referrer session (referrer_* set, no prospect)
   *  renders the referrer name + a "Referrer" chip and replies via the Phase A send. */
  contact_type?: string | null;
  referrer_id?: string | null;
  referrer_name?: string | null;
  /** The matched referrer's do_not_text flag → the composer's warn-and-allow banner. */
  referrer_do_not_text?: boolean | null;
}

/** The session-keyed thread read ({ session, messages }) — used for REFERRER sessions,
 *  which have no prospect personId to key the person-thread route on. */
export interface SessionThread {
  session: InboxSessionRow;
  messages: InboxThreadMessage[];
}

export interface PersonSmsThread {
  sessionId: string | null;
  /** 'ai_only' | 'human_takeover' | 'escalated' | 'completed' | null (no thread yet). */
  handoffStatus: string | null;
  /** 'active' | 'ended' | null — a terminal ('ended'/'completed') thread is read-only. */
  status: string | null;
  messages: InboxThreadMessage[];
}

export const crmInboxApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // The hub list, community-scoped + lead-gated. bucket=main (default) → the
    // operator's resolved-community conversations; bucket=unassigned → the triage tab
    // (no-community leads + ambiguous holds). RTK caches per-arg, so each bucket is a
    // separate cache entry under the shared CrmInboxList tag.
    getInboxSessions: builder.query<
      { sessions: InboxSessionRow[] },
      { instanceId: string; bucket?: "main" | "unassigned"; communityId?: string }
    >({
      query: ({ instanceId, bucket, communityId }) => ({
        url: "/crm/inbox/sessions",
        // communityId is sent ONLY for the Main bucket (the global filter narrows Main, never the
        // triage queue) - so the Unassigned query never re-keys/refetches on a community change.
        params: {
          instanceId,
          ...(bucket ? { bucket } : {}),
          ...(communityId && bucket !== "unassigned" ? { communityId } : {}),
        },
      }),
      providesTags: ["CrmInboxList"],
    }),

    // Scoped Main-unread count for the nav-tab badge (matches the filtered Main list).
    getCrmUnreadCount: builder.query<
      { count: number },
      { instanceId: string; communityId?: string }
    >({
      query: ({ instanceId, communityId }) => ({
        url: "/crm/inbox/unread-count",
        params: { instanceId, ...(communityId ? { communityId } : {}) },
      }),
      providesTags: ["CrmUnreadCount"],
    }),

    // The Unassigned tab's own backlog count (NOT on the nav badge — D3).
    getCrmUnassignedCount: builder.query<{ count: number }, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/crm/inbox/unassigned-count", params: { instanceId } }),
      providesTags: ["CrmUnassignedCount"],
    }),

    // Triage: link an Unassigned conversation to a prospect (server gates on the
    // resolved-prospect community + returns structured 409s on conflict). On success the
    // conversation moves out of Unassigned into Main, so refresh both buckets + counts.
    // Two modes (exactly one of prospectId | communityId): LINK a prospect (ambiguous
    // candidate) or RESOLVE the community on the existing linked lead (no-community lead).
    assignConversation: builder.mutation<
      {
        success: boolean;
        sessionId: string;
        communityId: string | null;
        bucket: "main" | "unassigned";
        prospectId?: string;
      },
      { sessionId: string; instanceId: string; prospectId?: string; communityId?: string }
    >({
      query: ({ sessionId, instanceId, prospectId, communityId }) => ({
        url: `/crm/inbox/sessions/${sessionId}/assign`,
        method: "POST",
        params: { instanceId },
        body: communityId ? { communityId } : { prospectId },
      }),
      invalidatesTags: ["CrmInboxList", "CrmUnassignedCount", "CrmUnreadCount"],
    }),

    getPersonSmsThread: builder.query<PersonSmsThread, { personId: string; instanceId: string }>({
      query: ({ personId, instanceId }) => ({
        url: `/crm/inbox/person/${personId}/thread`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { personId }) => [{ type: "CrmInboxThread", id: personId }],
    }),

    // COMMS-2c: session-keyed thread read for a REFERRER session (no prospect personId).
    // Tagged by sessionId so a referrer reply invalidates it.
    getSessionThread: builder.query<SessionThread, { sessionId: string; instanceId: string }>({
      query: ({ sessionId, instanceId }) => ({
        url: `/crm/inbox/sessions/${sessionId}/messages`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { sessionId }) => [{ type: "CrmInboxThread", id: sessionId }],
    }),

    // Mark a conversation read (operator opened it) — decrements the badge.
    markInboxRead: builder.mutation<
      { success: boolean },
      { sessionId: string; instanceId: string }
    >({
      query: ({ sessionId, instanceId }) => ({
        url: `/crm/inbox/sessions/${sessionId}/read`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmUnreadCount", "CrmInboxList"],
    }),

    sendInboxReply: builder.mutation<
      { success: boolean; messageId: string; messageSid: string | null },
      { sessionId: string; instanceId: string; content: string; personId: string }
    >({
      query: ({ sessionId, instanceId, content }) => ({
        url: `/crm/inbox/sessions/${sessionId}/reply`,
        method: "POST",
        params: { instanceId },
        body: { content },
      }),
      // Invalidate BOTH the person-keyed (prospect) and session-keyed (referrer) thread
      // tags — one of the two matches whichever thread view is open.
      invalidatesTags: (_r, _e, { personId, sessionId }) => [
        { type: "CrmInboxThread", id: personId },
        { type: "CrmInboxThread", id: sessionId },
        "CrmInboxList",
        "CrmUnreadCount",
      ],
    }),

    deleteInboxMessage: builder.mutation<
      { success: boolean; deletedBy: string | null },
      { sessionId: string; messageId: string; instanceId: string; personId: string }
    >({
      query: ({ sessionId, messageId, instanceId }) => ({
        url: `/crm/inbox/sessions/${sessionId}/messages/${messageId}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { personId }) => [{ type: "CrmInboxThread", id: personId }],
    }),

    // Explicit take over (join) / hand back to AI (leave) — the advisor-style toggle.
    setInboxHandoff: builder.mutation<
      { success: boolean; handoffStatus: string },
      { sessionId: string; instanceId: string; action: "join" | "leave"; personId: string }
    >({
      query: ({ sessionId, instanceId, action }) => ({
        url: `/crm/inbox/sessions/${sessionId}/handoff`,
        method: "POST",
        params: { instanceId },
        body: { action },
      }),
      invalidatesTags: (_r, _e, { personId }) => [
        { type: "CrmInboxThread", id: personId },
        "CrmInboxList",
      ],
    }),
  }),
});

export const {
  useGetInboxSessionsQuery,
  useGetCrmUnreadCountQuery,
  useGetCrmUnassignedCountQuery,
  useAssignConversationMutation,
  useGetPersonSmsThreadQuery,
  useGetSessionThreadQuery,
  useMarkInboxReadMutation,
  useSendInboxReplyMutation,
  useDeleteInboxMessageMutation,
  useSetInboxHandoffMutation,
} = crmInboxApi;
